import type { AnimalItem, ShareItem } from "../mocks/animals";

type AnimalLike = Pick<
  AnimalItem,
  | "siraNo"
  | "kupeNo"
  | "kg"
  | "referansFiyat"
  | "toplamHisse"
  | "satirlanHisse"
  | "toplamSatisTutari"
  | "toplamOdenen"
  | "hisseliSatisEtiketi"
  | "disKesim"
  | "hisseler"
  | "kesimGunu"
>;

export type AnimalSalesType = "Hisseli" | "Hissesiz" | "Dış Kesim";
export type AnimalSaleStatus = "SATILDI" | "DEVAM" | "SATIŞ YOK";
export type AnimalKgRefMode = "KG Satış" | "Normal Satış";
export type AnimalPaymentStatus = "Tamamlanan" | "Bekleyen";

export function normalizeText(value?: string) {
  return (value ?? "").trim().toLocaleLowerCase("tr-TR");
}

export function normalizePhone(value?: string) {
  return (value ?? "").replace(/\D/g, "");
}

export function getDisplayKupeNo(animal: Pick<AnimalItem, "siraNo">) {
  const value = Number(animal.siraNo ?? 0);
  return value > 0 ? String(value) : "—";
}

export function getDisplaySiparisNo(animal: Pick<AnimalItem, "kupeNo">) {
  const value = String(animal.kupeNo ?? "").trim();
  return value || "—";
}

export function getAnimalSalesType(animal: AnimalLike): AnimalSalesType {
  if (animal.disKesim) return "Dış Kesim";

  if (typeof animal.hisseliSatisEtiketi === "boolean") {
    return animal.hisseliSatisEtiketi ? "Hisseli" : "Hissesiz";
  }

  return Number(animal.toplamHisse ?? 0) > 1 ? "Hisseli" : "Hissesiz";
}

export function getAnimalSaleStatus(animal: Pick<AnimalItem, "toplamHisse" | "satirlanHisse">): AnimalSaleStatus {
  const sold = Number(animal.satirlanHisse ?? 0);
  const total = Math.max(0, Number(animal.toplamHisse ?? 0));

  if (sold <= 0) return "SATIŞ YOK";
  if (total > 0 && sold >= total) return "SATILDI";
  return "DEVAM";
}

export function getAnimalKgRefMode(animal: Pick<AnimalItem, "kg" | "referansFiyat">): AnimalKgRefMode {
  return animal.kg || animal.referansFiyat ? "KG Satış" : "Normal Satış";
}

export function getAnimalKgRefText(animal: Pick<AnimalItem, "kg" | "referansFiyat">) {
  const kg = animal.kg ? `${animal.kg} kg` : "—";
  const ref = animal.referansFiyat ? `${animal.referansFiyat} ₺` : "—";
  return `${kg} / ${ref}`;
}

export function getAnimalMissingShareCount(animal: Pick<AnimalItem, "toplamHisse" | "satirlanHisse">) {
  return Math.max(0, Number(animal.toplamHisse ?? 0) - Number(animal.satirlanHisse ?? 0));
}

export function getShareTotalCharge(share: ShareItem) {
  return (
    Number(share.tutar ?? 0) +
    Number(share.kesimUcreti ?? 0) +
    Number(share.parcalamaUcreti ?? 0) +
    Number(share.tasimaUcreti ?? 0) -
    Number(share.indirim ?? 0)
  );
}

export function getShareRemainingBalance(share: ShareItem) {
  return getShareTotalCharge(share) - Number(share.odenen ?? 0);
}

export function getAnimalTotalCharge(
  animal: Pick<AnimalItem, "toplamSatisTutari" | "hisseler">
) {
  const shares = Array.isArray(animal.hisseler) ? animal.hisseler : [];

  if (shares.length > 0) {
    return shares.reduce((sum, share) => sum + getShareTotalCharge(share), 0);
  }

  return Number(animal.toplamSatisTutari ?? 0);
}

export function getAnimalPaidTotal(
  animal: Pick<AnimalItem, "toplamOdenen" | "hisseler">
) {
  const shares = Array.isArray(animal.hisseler) ? animal.hisseler : [];

  if (shares.length > 0) {
    return shares.reduce((sum, share) => sum + Number(share.odenen ?? 0), 0);
  }

  return Number(animal.toplamOdenen ?? 0);
}

export function getAnimalRemainingBalance(
  animal: Pick<AnimalItem, "toplamSatisTutari" | "toplamOdenen" | "hisseler">
) {
  return getAnimalTotalCharge(animal) - getAnimalPaidTotal(animal);
}

export function getAnimalFinanceStatus(
  animal: Pick<AnimalItem, "toplamSatisTutari" | "toplamOdenen" | "hisseler">
): "Borçlu" | "Tamamlanan" {
  return getAnimalRemainingBalance(animal) > 0 ? "Borçlu" : "Tamamlanan";
}

export function getAnimalPaymentStatus(
  animal: Pick<AnimalItem, "hisseler">
): AnimalPaymentStatus {
  const shares = Array.isArray(animal.hisseler) ? animal.hisseler : [];
  const soldShares = shares
    .filter((share): share is ShareItem => Boolean(share && typeof share === "object"))
    .filter((share) => Boolean(String(share.musteriAdi ?? "").trim()));

  if (soldShares.length === 0) return "Bekleyen";

  return soldShares.every((share) => getShareRemainingBalance(share) <= 0)
    ? "Tamamlanan"
    : "Bekleyen";
}

export function hasTransportForShare(share: ShareItem) {
  if (typeof share.tasimaVar === "boolean") {
    return share.tasimaVar;
  }

  return Number(share.tasimaUcreti ?? 0) > 0;
}

export function hasTransportForAnimal(animal: Pick<AnimalItem, "hisseler">) {
  return (animal.hisseler ?? []).some(hasTransportForShare);
}

export function getAnimalCustomerSummary(animal: Pick<AnimalItem, "hisseler">) {
  const names = Array.from(
    new Set(
      (animal.hisseler ?? [])
        .map((share) => share.musteriAdi?.trim())
        .filter((value): value is string => Boolean(value))
    )
  );

  if (names.length === 0) return "—";
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}

export function getAnimalCustomerSearchText(animal: Pick<AnimalItem, "hisseler">) {
  return (animal.hisseler ?? [])
    .flatMap((share) => [share.musteriAdi ?? "", share.telefon ?? ""])
    .join(" | ");
}

export function formatDateLabel(value?: string) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("tr-TR");
  } catch {
    return value;
  }
}
